 // RB-MY01-PalmGarden.web/src/pages/Plants.tsx
import React, { useState, useEffect } from 'react';
import { useApi } from '../services/api';
import { Link } from 'react-router-dom';
import { 
  Typography, 
  Box, 
  Card, 
  CardContent, 
  CardActions, 
  Button, 
  TextField,
  InputAdornment,
  CircularProgress,
  Fab,
  Divider,
  Paper
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { GridContainer, GridItem } from '../components/CustomGrid';

interface Plant {
  id: number;
  name: string;
  type: string;
  waterFrequency: number;
  lastWatered?: string;
  nextWateringDue?: string;
}

const Plants: React.FC = () => {
  const api = useApi();
  const [plants, setPlants] = useState<Plant[]>([]);
  const [filteredPlants, setFilteredPlants] = useState<Plant[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchPlants = async () => {
      try {
        setLoading(true);
        // In a real app, you would fetch this from your API
        // const data = await api.plants.getPlants();
        
        // For now, use mock data
        const mockData: Plant[] = [
          {
            id: 1,
            name: "Monstera Deliciosa",
            type: "Tropical",
            waterFrequency: 7,
            lastWatered: "2023-04-01",
            nextWateringDue: "2023-04-08"
          },
          {
            id: 2,
            name: "Snake Plant",
            type: "Succulent",
            waterFrequency: 14,
            lastWatered: "2023-04-03",
            nextWateringDue: "2023-04-17"
          },
          {
            id: 3,
            name: "Peace Lily",
            type: "Flowering",
            waterFrequency: 7,
            lastWatered: "2023-04-05",
            nextWateringDue: "2023-04-12"
          },
          {
            id: 4,
            name: "Fiddle Leaf Fig",
            type: "Tree",
            waterFrequency: 10,
            lastWatered: "2023-04-02",
            nextWateringDue: "2023-04-12"
          },
          {
            id: 5,
            name: "Pothos",
            type: "Vine",
            waterFrequency: 10,
            lastWatered: "2023-04-04",
            nextWateringDue: "2023-04-14"
          }
        ];
        
        setPlants(mockData);
        setFilteredPlants(mockData);
      } catch (err) {
        console.error("Error fetching plants:", err);
        setError("Failed to load plants");
      } finally {
        setLoading(false);
      }
    };
    
    fetchPlants();
  }, []);
  
  // Filter plants when search term changes
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredPlants(plants);
      return;
    }
    
    const filtered = plants.filter(plant => 
      plant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      plant.type.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    setFilteredPlants(filtered);
  }, [searchTerm, plants]);
  
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          My Plants
        </Typography>
        
        <TextField
          placeholder="Search plants..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: { xs: '100%', sm: '300px' } }}
        />
      </Box>
      
      <Divider sx={{ mb: 3 }} />
      
      {filteredPlants.length === 0 ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          p: 4,
          textAlign: 'center'
        }}>
          <Typography variant="h6" gutterBottom>
            {plants.length === 0 ? 'No plants found' : 'No plants match your search'}
          </Typography>
          
          {plants.length === 0 && (
            <Button 
              component={Link} 
              to="/plants/add" 
              variant="contained"
              startIcon={<AddIcon />}
              sx={{ mt: 2 }}
            >
              Add Your First Plant
            </Button>
          )}
        </Box>
      ) : (
        <GridContainer spacing={3}>
          {filteredPlants.map(plant => (
            <GridItem xs={12} sm={6} md={4} lg={3} key={plant.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" component="h2" gutterBottom noWrap>
                    {plant.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {plant.type}
                  </Typography>
                  <Typography variant="body2">
                    Watering: Every {plant.waterFrequency} days
                  </Typography>
                  {plant.nextWateringDue && (
                    <Typography 
                      variant="body2" 
                      color={new Date(plant.nextWateringDue) <= new Date() ? 'error' : 'inherit'}
                      sx={{ mt: 1 }}
                    >
                      Next watering: {new Date(plant.nextWateringDue).toLocaleDateString()}
                    </Typography>
                  )}
                </CardContent>
                <CardActions>
                  <Button 
                    size="small" 
                    component={Link} 
                    to={`/plants/${plant.id}`}
                  >
                    View Details
                  </Button>
                  <Button 
                    size="small" 
                    component={Link} 
                    to={`/plants/edit/${plant.id}`}
                  >
                    Edit
                  </Button>
                </CardActions>
              </Card>
            </GridItem>
          ))}
        </GridContainer>
      )}
      
      {/* Floating action button to add new plant */}
      <Fab 
        color="primary" 
        aria-label="add" 
        component={Link} 
        to="/plants/add"
        sx={{ 
          position: 'fixed', 
          bottom: 16, 
          right: 16 
        }}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
};

export default Plants;